import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InventoryEntryDetailTableModel } from 'src/app/models/tables/inventory/inventory-entry-detail-table.model';
import { InventoryEntryTableModel } from 'src/app/models/tables/inventory/inventory-entry-table.model';
import { PurchaseOrderPendingTableModel } from 'src/app/models/tables/inventory/purchase-order-pending-table.model';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { InventoryEntryDetailService } from 'src/app/services/inventory/inventory-entry-detail.service';
import { InventoryEntryService } from 'src/app/services/inventory/inventory-entry.service';
import { HeaderService } from 'src/app/services/header/header.service';

@Component({
  selector: 'app-inventory-entry',
  templateUrl: './inventory-entry.component.html',
  styleUrls: ['./inventory-entry.component.scss'],
  standalone: false,
})
export class InventoryEntryComponent implements OnInit {
  generalHeader: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<InventoryEntryTableModel> = new PagedResponseModel<InventoryEntryTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  detailData: PagedResponseModel<InventoryEntryDetailTableModel> = new PagedResponseModel<InventoryEntryDetailTableModel>();
  nameWindow: string = 'Entradas de Inventario';
  purchaseOrderPendingHeader: ColumnResponseModel[] = [];
  purchaseOrderPendingData: PagedResponseModel<PurchaseOrderPendingTableModel> = new PagedResponseModel<PurchaseOrderPendingTableModel>();
  projectName = ProjectName.projectName;

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  filters: ColumnResponseModel[] = [];
  page: number = 1;
  limit: number = 10;
  listObservers: Array<Subscription> = new Array<Subscription>();
  previousSort: { field: string; isAscsendant: number };
  swPurchaseOrder: boolean = false;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit',
    },
  ];

  constructor(
    private inventoryEntryService: InventoryEntryService,
    private inventoryEntryDetailService: InventoryEntryDetailService,
    private router: Router,
    private title: Title,
    private headerService: HeaderService,
    private userAuthenticatedService: UserAuthenticatedService
  ) { }

  async ngOnInit(): Promise<void> {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);

    this.getGeneralParametersByCompany();

    const filters = localStorage.getItem('inventory-entry');
    filters ? (this.filters = JSON.parse(filters)) : [];
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getInventoryEntriesForGeneralConsultation(),
      this.getGeneralParametersByCompany(),
    ]);
  }

  ngOnDestroy() {
    this.listObservers.forEach((sub) => sub.unsubscribe());
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(
        this.inventoryEntryDetailService.exportDetailConsultation(
          this.filters,
          this.columnSort,
          this.ascendant
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Entradas Inventario Detalle.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(
        'Error al exportar las entradas de inventario detalle.',
        error
      );
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(
        this.inventoryEntryService.exportGeneralConsultation(
          this.filters,
          this.columnSort,
          this.ascendant
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Entradas Inventario.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las entradas de inventario.', error);
    }
  }

  async exportPurchaseOrderPending() {
    try {
      const blob = await firstValueFrom(
        this.inventoryEntryDetailService.exportPurchaseOrderPendingConsultation(
          this.filters,
          this.columnSort,
          this.ascendant
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Orden Compra Pendientes.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(
        'Error al exportar las ordenes de compra pendiente.',
        error
      );
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(
          this.inventoryEntryDetailService.getColumnsForDetailConsultation()
        );
      }
    } catch (error) {
      console.error(
        'Error al buscar las columnas para la consulta detalle.',
        error
      );
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(
          this.inventoryEntryService.getColumnsForGeneralConsultation()
        );
      }
    } catch (error) {
      console.error(
        'Error al buscar las columnas para la consulta general.',
        error
      );
    }
  }

  async getColumnsForPurchaseOrderPending() {
    try {
      if (this.purchaseOrderPendingHeader.length === 0) {
        this.purchaseOrderPendingHeader = await firstValueFrom(
          this.inventoryEntryDetailService.getColumnsForPurchaseOrderPendingConsultationAsync()
        );
      }
    } catch (error) {
      console.error(
        'Error al buscar las columnas para la ordenes de compra pendientes.',
        error
      );
    }
  }

  async getGeneralParametersByCompany() {
    const load =
      this.userAuthenticatedService.generalParameterObservable.subscribe(
        (response) => {
          this.swPurchaseOrder = response.some(
            (x) => x.name === 'ModuloCompras'
          );
        }
      );
    this.listObservers.push(load);
  }

  async getInventoryEntriesForGeneralConsultation() {
    try {
      this.generalData = await firstValueFrom(
        this.inventoryEntryService.getInventoryEntriesForGeneralConsultation(
          this.page,
          this.limit,
          this.columnSort,
          this.ascendant,
          this.filters
        )
      );
    } catch (error) {
      console.error('Error al buscar las entradas de inventario.', error);
    }
  }

  async getInventoryEntryDetailsForDetailConsultation() {
    try {
      this.detailData = await firstValueFrom(
        this.inventoryEntryDetailService.getInventoryEntryDetailsForDetailConsultation(
          this.page,
          this.limit,
          this.columnSort,
          this.ascendant,
          this.filters
        ));
    } catch (error) {
      console.error('Error al buscar la consulta detalle.', error);
    }
  }

  async getPurchaseOrderPendingConsultation() {
    try {
      this.purchaseOrderPendingData = await firstValueFrom(
        this.inventoryEntryDetailService.getPurchaseOrderPendingConsultation(
          this.page,
          this.limit,
          this.columnSort,
          this.ascendant,
          this.filters
        )
      );
    } catch (error) {
      console.error(
        'Error al buscar los detalles de las ordenes de compra pendientes.',
        error
      );
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length =
      this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length =
      this.detailHeader.length === 1 ? 0 : this.detailHeader.length;
    this.purchaseOrderPendingHeader.length =
      this.purchaseOrderPendingHeader.length === 1 ? 0 : this.purchaseOrderPendingHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getInventoryEntriesForGeneralConsultation(),
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10,
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getInventoryEntryDetailsForDetailConsultation(),
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10,
        };
        break;
      case 2:
        await Promise.all([
          this.getColumnsForPurchaseOrderPending(),
          this.getPurchaseOrderPendingConsultation(),
        ]);
        this.purchaseOrderPendingData = {
          ...this.purchaseOrderPendingData,
          currentPage: 1,
          pageSize: 10,
        };
        break;
    }
  }

  newInventoryEntry() {
    this.router.navigate(['/inventory-entry/register']);
  }

  onAction(event: {
    action: string;
    item?: InventoryEntryTableModel;
    selectedItems?: number[];
  }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([
        `/inventory-entry/register/${event.item.inventoryEntryId}`,
      ]);
    }
  }

  async onPageChange(event: {
    page: number;
    pageSize: number;
    sortField: string | undefined;
    sortOrder: number;
  }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getInventoryEntriesForGeneralConsultation();
        break;
      case 1:
        await this.getInventoryEntryDetailsForDetailConsultation();
        break;
      case 2:
        await this.getPurchaseOrderPendingConsultation();
        break;
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
      case 2:
        await this.exportPurchaseOrderPending();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);

    // Procesar cada filtro
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  private removeDuplicates(filters: any[]): any[] {
    return filters.filter(
      (value, index, self) =>
        index ===
        self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  private processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (
      valueFilter === null ||
      valueFilter === undefined ||
      valueFilter === '' ||
      valueFilter === 0
    ) {
      this.filters = this.filters.filter(
        (f) => f.propertyName !== propertyName
      );
    } else {
      const existingFilter = this.filters.find(
        (f) => f.propertyName === propertyName
      );
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }
}
